import { HttpClient } from '@angular/common/http';
import { Component, OnInit, ViewChild } from '@angular/core';
import PouchDB from 'pouchdb';
import { AuthenticationService } from '../services/authentication.service';
import { UserStorageService } from '../services/storage/user-storage.service';
import { environment } from 'src/environments/environment';
import { CommonDataService } from '../services/storage/common-data.service';
import { FisheryNoticesService } from '../services/storage/fishery-notices.service';
import { Router } from '@angular/router';
import { SharedMethodsService } from '../services/shared-methods.service';
import { MatChipListbox, MatChipListboxChange } from '@angular/material/chips';
import { map } from 'rxjs/operators';
import { MatTable, MatTableDataSource } from '@angular/material/table';
import { MatPaginator } from '@angular/material/paginator';
import { Logbook } from '../data-models/user-models/logbook.model';
import { MatDialog, MatDialogRef } from '@angular/material/dialog';
import { AddLogbookDialogComponent } from '../dialogs/add-logbook-dialog/add-logbook-dialog.component';
import { VesselData } from '../data-models/user-models/vessel-data.model';
import { EditMappingDialogComponent } from '../dialogs/edit-mapping-dialog/edit-mapping-dialog.component';
import { TripsDialogComponent, TripsDialogData } from './trips-dialog/trips-dialog.component';
import { MatSort } from '@angular/material/sort';

type UserRow = {
  name: string;
  fin: string;
  email: string;
}

export type MappingRow = {
  fin: string;
  vrn: string;
  logbookNum: string;
}

//Parse responses from various function app calls
//Toasts for various operations
//Edit logbooks

type DeactivateSeasonResponse = {
  ok: boolean;
  dryRun: boolean;
  year: number;
  gearNames: string[];
  scannedUserDocs: number;
  updatedUserDocs: number;
  updatedUserLogbooks: number;
  backfilledSeasonYear: number;
  backfilledIsActive: number;
};


@Component({
  selector: 'app-admin-panel',
  templateUrl: './admin-panel.component.html',
  styleUrls: ['./admin-panel.component.css'],
  standalone: false
})
export class AdminPanelComponent implements OnInit {

  filterActive: boolean = false;
  showUserList: boolean = false;
  userList!: MatTableDataSource<UserRow>;
  private _userSort?: MatSort;
  @ViewChild('userSort') set userSort(sort: MatSort) {
    this._userSort = sort;
    if (this.userList && sort) {
      this.userList.sort = sort;
    }
  }
  // Hold the selected row object, not its index: the rendered row order is
  // filtered *and* sorted, while filteredData is only filtered, so an index
  // taken from the template points at the wrong user once the table is sorted.
  selectedUser: UserRow | null = null;
  replicating: boolean = false;
  displayedUserColumns: string[] = ['name', 'fin', 'email'];

  logbookData!: MatTableDataSource<Logbook>;
  @ViewChild('logbookTable') logbookTable!: MatTable<Logbook>;
  @ViewChild('logbookPaginator') paginator!: MatPaginator;
  @ViewChild(MatSort) sort!: MatSort;

  finVrnMappingData!: MatTableDataSource<MappingRow>;
  @ViewChild('finVrnTable') finVrnTable!: MatTable<MappingRow>;
  vrnLogbookMappingData!: MatTableDataSource<MappingRow>;
  @ViewChild('vrnLbTable') vrnLbTable!: MatTable<MappingRow>;

  showLogbooks: boolean = true;
  allLogbooksColumns: string[] = ['gearname', 'logbook#', 'vname', 'vrn', 'seasonYear'];
  displayedVrnFin: string[] = ['fin', 'vrn'];
  displayedVrnLogbooks: string[] = ['vrn', 'logbook#'];

  logbookYears: number[] = [];
  selectedLogbookYear: number | 'all' = new Date().getFullYear();
  private logbookSearchText: string = '';

  // Season deactivation UI state
  deactivateSeasonYear: number = new Date().getFullYear() - 1;
  deactivationGearOptions: string[] = ['Gill Net', 'Seine', 'Troll'];
  deactivationGearSelected: Record<string, boolean> = {
    'Gill Net': false,
    'Seine': false,
    'Troll': false,
  };
  dryRunResult?: DeactivateSeasonResponse;
  dryRunBusy: boolean = false;
  deactivateBusy: boolean = false;

  constructor(
    public router: Router,
    public http: HttpClient,
    public authenticationService: AuthenticationService,
    public sharedMethodsService: SharedMethodsService,
    private dialog: MatDialog
  ) { }

  ngOnInit(): void {
    this.loadLogbookMappings();

    this.http.get<any[]>(environment.getUserList)
      .pipe(map(user => (user.map(v => ({ name: v.value[0], fin: v.value[1], email: v.key })))))
      .subscribe(x => {
        this.userList = new MatTableDataSource(x);
        // Case-insensitive sorting so 'apple' and 'Banana' interleave
        // rather than splitting into separate upper/lower-case sections.
        this.userList.sortingDataAccessor = (row, columnId) => {
          const value = (row as any)[columnId];
          return typeof value === 'string' ? value.toLowerCase() : value;
        };
        if (this._userSort) {
          this.userList.sort = this._userSort;
        }
      });
  }

  private loadLogbookMappings() {
    this.http.get<any>(environment.getLogbookMappings).subscribe(x => {
      let rows: any[] = x.rows ?? [];

      const finIdx = rows.findIndex(r => r?.doc?._id === 'fin_vrn_mappings');
      const finDoc = finIdx >= 0 ? rows.splice(finIdx, 1)[0]?.doc : null;
      const fin_vrn_mappings: any[] = finDoc?.fin_to_vrns ?? [];

      this.logbookData = new MatTableDataSource(
        rows
          .filter((row: any) => row?.doc?.logbook)
          .map((row: any) => Logbook.transformAnonymous(row.doc.logbook))
          .sort((a, b) => b.seasonYear - a.seasonYear) // Pre-sort by seasonYear descending
      );

      const yearsInData = this.logbookData.data
        .map(lb => lb.seasonYear)
        .filter(y => Number.isFinite(y));
      this.logbookYears = Array.from(new Set([new Date().getFullYear(), ...yearsInData]))
        .sort((a, b) => b - a);

      this.logbookData.filterPredicate = (logbook: Logbook, _filter: string) => {
        if (this.selectedLogbookYear !== 'all' && logbook.seasonYear !== this.selectedLogbookYear) {
          return false;
        }
        const search = this.logbookSearchText;
        if (!search) return true;
        const haystack = [
          logbook.gearName,
          logbook.logbookID,
          logbook.vessel?.vesselName,
          logbook.vessel?.vesselRegistrationNumber,
          logbook.seasonYear
        ].map(v => (v ?? '').toString().toLowerCase()).join(' ');
        return haystack.includes(search);
      };

      this.vrnLogbookMappingData = new MatTableDataSource(
        this.logbookData.data
          .filter(lb => lb.vessel.vesselRegistrationNumber != '')
          .map(lb => ({ fin: '', vrn: lb.vessel.vesselRegistrationNumber, logbookNum: lb.logbookID }))
      );

      const finVrnRows: MappingRow[] = (fin_vrn_mappings ?? []).reduce((acc: MappingRow[], a: any) => {
        const vrns = (a?.vrns ?? []) as any[];
        vrns.forEach((b: any) => acc.push({ fin: a.fin, vrn: b, logbookNum: '' }));
        return acc;
      }, []);

      this.finVrnMappingData = new MatTableDataSource(finVrnRows);

      this.vrnLogbookMappingData.filterPredicate = (data, filterString) =>
        filterString.split('|').some(s => data.vrn.includes(s));

      this.finVrnMappingData.filterPredicate = (data, filterString) =>
        filterString.split('|').some(s => data.vrn.includes(s));

      this.logbookData.paginator = this.paginator;

      this.applyLogbookFilter();

      // Set up sorting after data is loaded, with a timeout to ensure view is ready
      setTimeout(() => {
        this.logbookData.sort = this.sort;
        this.logbookData.sort.active = 'seasonYear';
        this.logbookData.sort.direction = 'desc';
        this.logbookData.sort.sortChange.emit({ active: 'seasonYear', direction: 'desc' });
      }, 0);
    });
  }


  chipMenuSelectionChange(box: MatChipListbox, event: MatChipListboxChange) {
    if (event.value == 'users') {
      this.showUserList = true;
      this.showLogbooks = false;
    } else if (event.value == 'logbooks') {
      this.showUserList = false;
      this.showLogbooks = true;
      this.logbookData.paginator = this.paginator;
    } else if (this.showUserList) {
      (box as any)._chips.first.select();
      (box as any)._chips.first.focus();
    } else {
      (box as any)._chips.last.select();
      (box as any)._chips.last.focus();
    }
  }


  filterUsers(event: Event) {
    const filterValue = (event.target as HTMLInputElement).value;
    this.userList.filter = filterValue.trim().toLowerCase();
    // Drop the selection if the filter hid the selected row, so the buttons
    // can't act on a user who is no longer visible.
    if (this.selectedUser && !this.userList.filteredData.includes(this.selectedUser)) {
      this.selectedUser = null;
    }
  }

  
  filterLogbooks(event: Event) {
    const filterValue = (event.target as HTMLInputElement).value;
    this.logbookSearchText = filterValue.trim().toLowerCase();
    this.applyLogbookFilter();
  }

  onLogbookYearChange(year: number | 'all') {
    this.selectedLogbookYear = year;
    this.applyLogbookFilter();
  }

  private applyLogbookFilter() {
    // filterPredicate reads logbookSearchText and selectedLogbookYear from `this`;
    // assigning to `filter` here is just what triggers MatTableDataSource to re-run it.
    this.logbookData.filter = `${this.logbookSearchText}|${this.selectedLogbookYear}`;
    this.filterActive = this.logbookSearchText !== '';
    this.vrnLogbookMappingData.filter = this.logbookData.filteredData
      .filter(fd => fd.vessel.vesselRegistrationNumber != '')
      .map(lb => lb.vessel.vesselRegistrationNumber)
      .join('|');
    this.finVrnMappingData.filter = this.vrnLogbookMappingData.filter;
  }


  replicateUserData() {
    const user = this.selectedUser;
    if (!user) return;

    let token = UserStorageService.adminUser!.apiKey;
    let userDbName = `${user.email.replace(/@/g, "_").replace(/%/g, "_").replace(/\./g, "_").replace(/\+/g, "_")}_trips`;

    let userRemote: PouchDB.Database = new PouchDB(`https://${token}@${environment.databaseName}/${userDbName}`)

    UserStorageService.adminUser!.adminReplicated = user.email;
    this.replicating = true;
    UserStorageService.adminSyncUserData(userRemote).then((success: boolean) => {
      if (success) {
        UserStorageService.getUserOnline(UserStorageService.adminUser!.userEmail).then(() => {
          FisheryNoticesService.checkForNotices(UserStorageService.adminUser!.userEmail).then(() => CommonDataService.pullCommonData());
          this.replicating = false;
          this.router.navigate([UserStorageService.currUser.currHomepage]);
        }).catch(err => {
          console.log('Failed to replicate user data');
          console.log(err);
        });
      }
      else {
        this.sharedMethodsService.toastSessionExpired();
        this.replicating = false;
      }
    });
  }


  addLogbookDialog(index: number): void {
    let isNew: boolean = false;
    if (index == this.logbookData.data.length) {
      isNew = true;
      this.logbookData.data.push(new Logbook());
    }

    // For a new logbook the row hasn't been filtered/paginated yet — pull it from `data` directly.
    // For edits, `index` is an offset into the filtered+paginated view.
    const logbookForDialog = isNew
      ? this.logbookData.data[this.logbookData.data.length - 1]
      : this.logbookData.filteredData[index];

    const dialogRef: MatDialogRef<AddLogbookDialogComponent, any> = this.dialog.open(AddLogbookDialogComponent, {
      width: '500px',
      height: '575px',
      data: { logbook: logbookForDialog, currLogbooks: this.logbookData.data }
    });

    dialogRef.afterClosed().subscribe((result: Logbook | string) => {
      if (result && result !== 'Back') {

        if (result == 'delete') {
          //Remove from logbook table and mappings table
          let removed = this.logbookData.filteredData.splice(index, 1)[0];
          this.vrnLogbookMappingData.data.splice(this.vrnLogbookMappingData.data.findIndex(r => r.vrn == removed.vessel.vesselRegistrationNumber && r.logbookNum == removed.logbookID), 1);
          this.deleteLogbook(removed);
        } else {
          //Add to logbook table
          //Add to vrn-logbook mappings if vessel present
          if (isNew) {
            this.logbookData.data[index] = result as Logbook;
            //Only if vessel entered
            if (this.logbookData.data[index].vessel.vesselRegistrationNumber != '')
              this.vrnLogbookMappingData.data.push({ fin: '', vrn: this.logbookData.data[index].vessel.vesselRegistrationNumber, logbookNum: this.logbookData.data[index].logbookID });
            this.submitNewLogbook(result as Logbook);
          } else {
            index = this.logbookData.data.findIndex(lb => lb.logbookID == (result as Logbook).logbookID);
            this.logbookData.data[index] = (result as Logbook);
            //Update mapping
            if ((result as Logbook).vessel.vesselRegistrationNumber == '')
              this.vrnLogbookMappingData.data = this.vrnLogbookMappingData.data.filter(row => row.logbookNum != this.logbookData.data[index].logbookID);
            else {
              //What if filtered?
              let mapping = this.vrnLogbookMappingData.data.find(row => row.logbookNum == this.logbookData.data[index].logbookID);
              if (mapping) mapping.vrn = this.logbookData.data[index].vessel.vesselRegistrationNumber;
              else
                this.vrnLogbookMappingData.data.push({ fin: '', vrn: this.logbookData.data[index].vessel.vesselRegistrationNumber, logbookNum: this.logbookData.data[index].logbookID });
            }
            this.submitLogbookEdit(result as Logbook);
          }

        }

        this.logbookData.paginator = this.paginator;
        this.logbookTable.dataSource = this.logbookData;
        this.logbookTable.renderRows();
        this.vrnLbTable.dataSource = this.vrnLogbookMappingData;
        this.vrnLbTable.renderRows();

        
      } else if (isNew) {
        this.logbookData.data.splice(this.logbookData.data.length - 1, 1);
      }
    });
    
  }


  addEditMapping(type: string, isNew: boolean, index?: number, value?: MappingRow) {

    this.dialog.open(EditMappingDialogComponent, {
      width: '550px',
      height: '400px',
      autoFocus: false,
      data: { vrns: this.vrnLogbookMappingData.filteredData.map(vl => vl.vrn), logbooks: this.logbookData.data, type: type, isNew: isNew, value: value, editedLbs: this.logbookData.data.filter(x => x.vessel.vesselRegistrationNumber == value?.vrn) }
    }).afterClosed().subscribe((result: { logbookIndexes: number[], fin: string, vesselNumber: string, delete: boolean }) => {
      if (result) {
        if (type == 'fin-vrn') {
          if (isNew) {
            this.finVrnMappingData.data.push({ fin: result.fin, vrn: result.vesselNumber, logbookNum: '' });
          } else if (result.delete) {
            this.finVrnMappingData.data.splice(index!, 1);
          } else {
            this.finVrnMappingData.data[index!] = { fin: result.fin, vrn: result.vesselNumber, logbookNum: '' }
          }
          this.submitFinVrnUpdates(result.fin);
          this.finVrnTable.renderRows();
        } else {

          this.vrnLogbookMappingData.data = this.vrnLogbookMappingData.data.filter(vl => vl.vrn != value?.vrn);
          this.logbookData.data.filter((lb: Logbook) => lb.vessel.vesselRegistrationNumber == value?.vrn).forEach((lb: Logbook) => lb.vessel = new VesselData());
          
          if (result.delete) {
            result.logbookIndexes.forEach((lb: number) => this.logbookData.data[lb].vessel = new VesselData());
            this.submitLogbookVrnUpdates(result.vesselNumber, [], true);
          } else {
            result.logbookIndexes.forEach((lb: number) => {
              this.vrnLogbookMappingData.data = this.vrnLogbookMappingData.data.filter(vlb => vlb.logbookNum != this.logbookData.data[lb].logbookID);
              this.vrnLogbookMappingData.data.push({ fin: '', vrn: result.vesselNumber, logbookNum: this.logbookData.data[lb].logbookID });
              this.logbookData.data[lb].vessel = new VesselData('', result.vesselNumber);
            });
            this.submitLogbookVrnUpdates(result.vesselNumber, this.logbookData.data.filter(lb => lb.vessel.vesselRegistrationNumber == result.vesselNumber));
          }
                 
          this.vrnLbTable.renderRows();
          this.logbookTable.renderRows();
        }
      }
    })
  }


  submitNewLogbook(logbook: Logbook) {
    this.http.post<any>(environment.postLogbook, { logbook: logbook, fins: this.finVrnMappingData.data.filter(fvm => fvm.vrn == logbook.vessel.vesselRegistrationNumber).map(fvm => fvm.fin) })
      .subscribe(_ => {
        this.sharedMethodsService.toastLogbookAdminSuccess(`Logbook ${logbook.logbookID} successfully created`);
      }, (err: any) => {
        console.log(err);
        this.sharedMethodsService.toastSomethingWentWrong();
      });
  }

  submitLogbookEdit(logbook: Logbook) {
    this.http.post<any>(environment.editLogbook, { logbook: logbook, fins: this.finVrnMappingData.data.filter(fvm => fvm.vrn == logbook.vessel.vesselRegistrationNumber).map(fvm => fvm.fin) })
      .subscribe(_ => {
        this.sharedMethodsService.toastLogbookAdminSuccess(`Logbook ${logbook.logbookID} edit completed`);
      }, (err: any) => {
        console.log(err);
        this.sharedMethodsService.toastSomethingWentWrong();
      });
  }

  deleteLogbook(logbook: Logbook) {
    this.http.post<any>(environment.deleteLogbook, {
      logbook: logbook,
      fins: this.finVrnMappingData.data.filter(fvm => fvm.vrn == logbook.vessel.vesselRegistrationNumber).map(fvm => fvm.fin)
    }).subscribe(_ => {
      this.sharedMethodsService.toastLogbookAdminSuccess(`Logbook ${logbook.logbookID} successfully deleted`);
    }, (err: any) => {
      console.log(err);
      this.sharedMethodsService.toastSomethingWentWrong();
    });
  }

  //Passing vessel names???
  submitLogbookVrnUpdates(vrn: string, logbooks: Logbook[], isDelete: boolean = false) {
    this.http.post<any>(environment.updatelogbookVRNMappings, {
      VRN: vrn,
      isDelete: isDelete,
      vesselName: this.logbookData.data.find(lb => lb.vessel.vesselRegistrationNumber == vrn)?.vessel.vesselName,
      fins: this.finVrnMappingData.data.filter(fvm => fvm.vrn == vrn).map(fvm => fvm.fin),
      logbooks: logbooks
    }).subscribe(_ => {
      this.sharedMethodsService.toastLogbookAdminSuccess(`Logbook-VRN update success`);
    }, (err: any) => {
      console.log(err);
      this.sharedMethodsService.toastSomethingWentWrong();
    });
  }

  submitFinVrnUpdates(fin: string) {
    let vrns = this.finVrnMappingData.data.filter(fvm => fvm.fin == fin).map(v => v.vrn);
    this.http.post<any>(environment.updateFinVrnMappings, { fin: fin, vrns: vrns, logbooks: this.logbookData.data.filter(lb => vrns.includes(lb.vessel.vesselRegistrationNumber)) }).subscribe(x => {
      this.sharedMethodsService.toastLogbookAdminSuccess(`FIN-VRN update success`);
    }, (err: any) => {
      console.log(err);
      this.sharedMethodsService.toastSomethingWentWrong();
    });
  }

  openTripsDialog() {
    const user = this.selectedUser;
    if (!user) return;

        // build exactly the same DB name you use for replicateUserData:
    const dbName = user.email.replace(/[@.%+]/g, '_') + '_trips';

    this.dialog.open<TripsDialogComponent, TripsDialogData>(TripsDialogComponent, {
      width: '800px',
      data: { dbName }
    });
  }

  //  Season deactivation helpers 

  private getSelectedDeactivationGears(): string[] {
    return this.deactivationGearOptions.filter(g => !!this.deactivationGearSelected[g]);
  }

  get hasAnyDeactivationGearSelected(): boolean {
    return this.getSelectedDeactivationGears().length > 0;
  }

  onDeactivateYearChange(event: Event) {
    const el = event.target as HTMLInputElement;
    const v = Number(el.value);
    if (Number.isFinite(v)) this.deactivateSeasonYear = v;
    this.dryRunResult = undefined;
  }

  toggleDeactivationGear(gear: string, checked: boolean) {
    this.deactivationGearSelected[gear] = checked;
    this.dryRunResult = undefined;
  }

  runDeactivationDryRun() {
    const gears = this.getSelectedDeactivationGears();
    if (!gears.length) {
      this.sharedMethodsService.toastSomethingWentWrong();
      return;
    }

    this.dryRunBusy = true;

    const payload = {
      year: this.deactivateSeasonYear,
      gearNames: gears,
      dryRun: true
    };

    this.http.post<DeactivateSeasonResponse>(environment.deactivateSeasonLogbooks, payload)
      .subscribe((res) => {
        this.dryRunResult = res;
        this.dryRunBusy = false;
        this.sharedMethodsService.toastLogbookAdminSuccess(
          `Dry run complete: ${res.updatedUserDocs} user docs, ${res.updatedUserLogbooks} user logbooks`
        );
      }, (err: any) => {
        console.log(err);
        this.dryRunBusy = false;
        this.sharedMethodsService.toastSomethingWentWrong();
      });
  }

  runDeactivation() {
    const gears = this.getSelectedDeactivationGears();
    if (!gears.length || !this.dryRunResult) {
      this.sharedMethodsService.toastSomethingWentWrong();
      return;
    }

    const msg =
      `Deactivate logbooks for season ${this.deactivateSeasonYear}?\n\n` +
      `Gears: ${gears.join(', ')}\n` +
      `Will update:\n` +
      `- ${this.dryRunResult.updatedUserDocs} user docs (${this.dryRunResult.updatedUserLogbooks} logbooks)\n` +
      `Also will backfill missing fields:\n` +
      `- seasonYear on ${this.dryRunResult.backfilledSeasonYear} logbooks\n` +
      `- isActive on ${this.dryRunResult.backfilledIsActive} logbooks\n\n` +
      `Proceed?`;


    if (!window.confirm(msg)) return;

    this.deactivateBusy = true;

    const payload = {
      year: this.deactivateSeasonYear,
      gearNames: gears,
      dryRun: false
    };

    this.http.post<DeactivateSeasonResponse>(environment.deactivateSeasonLogbooks, payload)
      .subscribe((res) => {
        this.deactivateBusy = false;
        this.dryRunResult = undefined;

        this.sharedMethodsService.toastLogbookAdminSuccess(
          `Deactivated: ${res.updatedUserDocs} user docs, ${res.updatedUserLogbooks} user logbooks`
        );

        this.loadLogbookMappings();
      }, (err: any) => {
        console.log(err);
        this.deactivateBusy = false;
        this.sharedMethodsService.toastSomethingWentWrong();
      });
  }
}
